import axios, { AxiosError, AxiosInstance } from 'axios';

import {
  adaptLeaderboardEntry,
  adaptMeToProfile,
  adaptParticipantProfile,
  adaptQuest,
  adaptSubmission,
  adaptSubmissionHistoryEntry,
  adaptSubmissionLimits,
  adaptTask,
  groupQuestsIntoSections,
  type BackendCompletionResult,
  type BackendLeaderboardEntry,
  type BackendMe,
  type BackendParticipantProfile,
  type BackendPublicConfig,
  type BackendQuest,
  type BackendSubmissionHistoryRow,
  type BackendSubmissionLimits,
  type BackendSubmissionRow,
  type BackendTask,
  type BackendVerifyResponse,
} from '@/lib/adapters';
import { readableApiError } from '@/lib/apiErrors';
import { PUBLIC_QUESTS } from '@/data/publicCatalogue';
import { useAuthStore } from '@/store/authStore';
import type {
  ClaimQuestResponse,
  ConversionRequest,
  LeaderboardEntry,
  ParticipantProfile,
  Quest,
  QuestSection,
  Referral,
  Submission,
  SubmissionCategory,
  SubmissionLimits,
  UserProfile,
  UserRole,
} from '@/lib/types';

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

// For calls a user is actively waiting on behind a spinner (starting a social
// link, linking Telegram, issuing the bot link code): fail fast enough to show
// an error instead of the 90 s client default meant for uploads and
// verification.
const INTERACTIVE_TIMEOUT_MS = 15_000;
const TOKEN_KEY = 'bigw_token';

// Every backend route lives under /v1 (single prefix, single error envelope),
// so the version is baked into the axios base URL rather than repeated per call.
const client: AxiosInstance | null = BASE_URL
  ? axios.create({ baseURL: `${BASE_URL.replace(/\/$/, '')}/v1`, timeout: 90_000 })
  : null;

if (client) {
  client.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  // Swap axios's "Request failed with status code N" for the reason bigw-api
  // sent, so every `err.message` shown in the UI is readable. The error stays
  // an AxiosError with its `response`, so status checks below still work.
  client.interceptors.response.use(undefined, (err: unknown) => {
    if (err instanceof AxiosError) err.message = readableApiError(err);
    return Promise.reject(err);
  });
}

// bigwater_backend ("rail2") — separate service, separate auth model (a raw
// Privy access token, not our rail1 session JWT). The Privy token is short-
// lived and fetched fresh in the React layer per call, so it's passed
// explicitly rather than auto-attached; the rail1 JWT, by contrast, lives in
// localStorage just like `client`'s, so it's auto-attached here as
// `x-rail1-token` (rail2 uses it to verify the caller is a linked rail1
// session, alongside the Privy `Authorization` header set per call below).
const BIGWATER_BASE_URL = (import.meta.env.VITE_BIGWATER_API_URL as string | undefined) ?? '';
const bigwaterClient: AxiosInstance | null = BIGWATER_BASE_URL
  ? axios.create({ baseURL: `${BIGWATER_BASE_URL.replace(/\/$/, '')}/api`, timeout: 90_000 })
  : null;

if (bigwaterClient) {
  bigwaterClient.interceptors.request.use((config) => {
    const rail1Token = localStorage.getItem(TOKEN_KEY);
    if (rail1Token) config.headers['x-rail1-token'] = rail1Token;
    return config;
  });
}

/** Whether bigwater_backend ("rail2") is configured for this build — gates
 *  any UI (e.g. the "Connect Telegram Bot" card) that depends on it. */
export function isBigwaterConfigured(): boolean {
  return bigwaterClient !== null;
}

/** Bot username for the `t.me/<bot>?start=<code>` deep link built after
 *  `createTelegramLinkCode` — build-time config, not served by any backend. */
export function getTelegramBotUsername(): string | undefined {
  return import.meta.env.VITE_TELEGRAM_BOT_USERNAME as string | undefined;
}

const MOCK_LATENCY_MS = 300;
const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

function isUnauthorized(err: unknown): boolean {
  return err instanceof AxiosError && err.response?.status === 401;
}

/** Backend rejection bodies are `{ error }` or `{ message }` — surface that
 *  text (e.g. the cooldown/daily-cap messages from submitProof) instead of
 *  axios's generic "Request failed with status code N". */
function extractBigwaterError(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { error?: string; message?: string } | undefined;
    return data?.error ?? data?.message ?? fallback;
  }
  return err instanceof Error ? err.message : fallback;
}

/** `/link-rail1` fails with `{ error: "no_profile", user: { role, ... } }`
 *  for admin/superadmin accounts (they have no participant profile row).
 *  Returns the role so callers can gate the app for them, or null for any
 *  other failure (network error, participant account, etc). */
export function extractAdminGateRole(err: unknown): UserRole | null {
  if (!(err instanceof AxiosError)) return null;
  const data = err.response?.data as { error?: string; user?: { role?: UserRole } } | undefined;
  if (data?.error !== 'no_profile') return null;
  const role = data.user?.role;
  return role === 'admin' || role === 'superadmin' ? role : null;
}

export interface LoginResponse {
  token: string;
  profile: UserProfile;
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// ─── Public config ──────────────────────────────────────────────────────────

let cachedConfig: BackendPublicConfig | null = null;
export async function fetchPublicConfig(): Promise<BackendPublicConfig | null> {
  if (!client) return null;
  if (cachedConfig) return cachedConfig;
  const { data } = await client.get<BackendPublicConfig>('/config');
  cachedConfig = data;
  return cachedConfig;
}

// ─── Auth (Privy → backend session) ─────────────────────────────────────────

/**
 * Exchange a Privy access token for our backend session JWT:
 *   POST /v1/auth/privy { access_token } -> { access_token: <JWT>, user }.
 *
 * The Privy modal and access-token retrieval happen in the React layer (see
 * ConnectButton, which calls `usePrivy().getAccessToken()`); this only does the
 * server-side handshake and persists the JWT. Callers hydrate the full profile
 * via `fetchProfile()` (/v1/auth/me) once the token is stored.
 */
// Single-flight guard: several ConnectButton instances can be mounted at once
// (header, hero, quest modal…) and Privy fires `onComplete` on every mounted
// `useLogin`, so one login used to POST /v1/auth/privy, GET /link-rail1, and
// GET /auth/me once per visible button. Concurrent callers of each of the
// three now share one in-flight call instead — see `linkRail1`/`fetchProfile`
// below for the same pattern applied to the other two.
let inflightLogin: Promise<string> | null = null;

export function loginWithPrivy(privyAccessToken: string): Promise<string> {
  if (!client) throw new Error('Backend not configured');
  if (inflightLogin) return inflightLogin;

  const axiosClient = client;
  inflightLogin = (async () => {
    try {
      const { data } = await axiosClient.post<BackendVerifyResponse>('/auth/privy', {
        access_token: privyAccessToken,
      });
      setStoredToken(data.access_token);
      return data.access_token;
    } finally {
      inflightLogin = null;
    }
  })();
  return inflightLogin;
}

export async function logout(): Promise<void> {
  clearStoredToken();
  // Backend has no explicit /auth/logout — JWT just expires.
}

/**
 * Link the current rail1 (bigw-api) account to a rail2 (bigwater_backend)
 * participant profile: GET /api/link-rail1. Sends rail1's own session JWT
 * (verified locally by rail2 via a shared secret, auto-attached as
 * `x-rail1-token` by the interceptor above) plus the raw Privy access token
 * (rail2's own auth, required by `privyAuth`/`linkRailOne` regardless of the
 * route being on `PublicRouter`). Idempotent on the backend, so safe to call
 * on every login/retry.
 */
let inflightRail1Link: Promise<{ linked: boolean; rail1UserId: string }> | null = null;

export function linkRail1(
  privyAccessToken: string,
): Promise<{ linked: boolean; rail1UserId: string }> {
  if (!bigwaterClient) throw new Error('bigwater_backend not configured');
  if (inflightRail1Link) return inflightRail1Link;

  const axiosClient = bigwaterClient;
  inflightRail1Link = (async () => {
    try {
      const { data } = await axiosClient.get('/link-rail1', {
        headers: { Authorization: `Bearer ${privyAccessToken}` },
      });
      return data;
    } finally {
      inflightRail1Link = null;
    }
  })();
  return inflightRail1Link;
}

// ─── Quests ─────────────────────────────────────────────────────────────────

/**
 * Adapt the visitor preview through the exact same pipeline as the authed list:
 * sort by `order_index` (backend does the same in SQL), map with `adaptQuest`,
 * then group into sections. `useQuests` locks the cards for disconnected clients.
 */
function publicSections(): QuestSection[] {
  const titlesByCode = new Map(PUBLIC_QUESTS.map((q) => [q.code, q.title]));
  const completedCodes = new Set(
    PUBLIC_QUESTS.filter((q) => q.completed).map((q) => q.code),
  );
  const adapted: Quest[] = [...PUBLIC_QUESTS]
    .sort((a, b) => a.order_index - b.order_index)
    .map((q) => adaptQuest(q, titlesByCode, completedCodes));
  return groupQuestsIntoSections(adapted);
}

export async function fetchQuests(): Promise<QuestSection[]> {
  // Visitors and disconnected clients see the public catalogue (a mirror of the
  // backend catalogue). `useQuests` will lock every card so they can browse but
  // not claim — clicking a card opens the connect gate.
  if (!client || !getStoredToken()) {
    return publicSections();
  }

  try {
    const { data } = await client.get<BackendQuest[]>('/quests');
    // Bonus quests name their prerequisites by code; resolve to titles and
    // completion state here, where the whole list is in hand.
    const titlesByCode = new Map(data.map((q) => [q.code, q.title]));
    const completedCodes = new Set(data.filter((q) => q.completed).map((q) => q.code));
    const adapted: Quest[] = data.map((q) => adaptQuest(q, titlesByCode, completedCodes));
    return groupQuestsIntoSections(adapted);
  } catch (err) {
    if (isUnauthorized(err)) {
      clearStoredToken();
      // The rest of the app (authStore) still thinks this session is good —
      // without this, Missions would render the visitor mock catalogue as a
      // real, claimable "available" list while every other page correctly
      // treats the session as dead.
      useAuthStore.getState().setAuthenticated(false);
      useAuthStore.getState().setProfile(null);
      return publicSections();
    }
    throw err;
  }
}

export async function claimQuest(id: string): Promise<ClaimQuestResponse> {
  if (!client) throw new Error('Backend not configured');
  if (!getStoredToken()) throw new Error('Not authenticated');

  const { data } = await client.post<BackendCompletionResult>(`/quests/${id}/complete`, {});
  // Optimistic shape: we don't re-fetch the full list — `useClaimQuest` will
  // invalidate the `['quests']` query so the next render pulls fresh data.
  // The Quest object returned here is only used by the modal for the success
  // animation, so a minimal stub with `status: 'completed'` is enough.
  const quest = {
    id,
    slug: id,
    title: '',
    description: '',
    category: 'onboarding',
    type: 'visit',
    points: data.points_awarded,
    status: 'completed',
    icon: 'check-circle',
    verifyMethod: 'auto',
  } as Quest;
  return { quest, pointsAwarded: data.points_awarded };
}

// ─── Profile (/v1/auth/me) ──────────────────────────────────────────────────

let inflightProfileFetch: Promise<UserProfile> | null = null;

export function fetchProfile(): Promise<UserProfile> {
  if (!client) throw new Error('Backend not configured');
  if (!getStoredToken()) throw new Error('Not authenticated');
  if (inflightProfileFetch) return inflightProfileFetch;

  const axiosClient = client;
  inflightProfileFetch = (async () => {
    try {
      const { data: me } = await axiosClient.get<BackendMe>('/auth/me');
      return adaptMeToProfile(me);
    } catch (err) {
      if (isUnauthorized(err)) {
        clearStoredToken();
      }
      throw err;
    } finally {
      inflightProfileFetch = null;
    }
  })();
  return inflightProfileFetch;
}

// ─── Leaderboard ────────────────────────────────────────────────────────────

export async function fetchLeaderboard(
  privyAccessToken: string,
  limit = 500,
): Promise<LeaderboardEntry[]> {
  if (!bigwaterClient) throw new Error('bigwater_backend not configured');
  const { data } = await bigwaterClient.get<{ leaderboard: BackendLeaderboardEntry[] }>(
    '/leaderboard',
    { headers: { Authorization: `Bearer ${privyAccessToken}` }, params: { limit } },
  );
  return data.leaderboard
    .filter((row) => row.walletAddress)
    .map(adaptLeaderboardEntry);
}


// ─── Referrals (NO BACKEND — flagged) ───────────────────────────────────────

/**
 * Backend gap: the API has no referrals endpoint. The Referrals page renders
 * mock data so the UI shape is preserved. Replace this once the backend
 * exposes /referrals/me (or equivalent).
 */
export async function fetchReferrals(): Promise<Referral> {
  await wait(MOCK_LATENCY_MS);
  const { MOCK_REFERRALS } = await import('@/data/mockReferrals');
  return MOCK_REFERRALS;
}

// ─── Submissions (proof of impact) ─────────────────────────────────────────
// bigwater_backend ("rail2") — every call needs a raw Privy access token
// (fetched in the React layer via usePrivy().getAccessToken(), same as
// linkRail1/useRail1Link), passed explicitly since bigwaterClient has no
// auto-attach interceptor.

export async function fetchSubmissionCategories(
  privyAccessToken: string,
): Promise<SubmissionCategory[]> {
  if (!bigwaterClient) throw new Error('bigwater_backend not configured');
  const { data } = await bigwaterClient.get<{ tasks: BackendTask[] }>('/tasks', {
    headers: { Authorization: `Bearer ${privyAccessToken}` },
  });
  return data.tasks.map(adaptTask);
}

export async function fetchSubmissionLimits(privyAccessToken: string): Promise<SubmissionLimits> {
  if (!bigwaterClient) throw new Error('bigwater_backend not configured');
  const { data } = await bigwaterClient.get<BackendSubmissionLimits>('/submissions/limits', {
    headers: { Authorization: `Bearer ${privyAccessToken}` },
  });
  return adaptSubmissionLimits(data);
}

export interface SubmitProofInput {
  category: SubmissionCategory;
  file: File;
}

export async function submitProof(
  privyAccessToken: string,
  input: SubmitProofInput,
): Promise<Submission> {
  if (!bigwaterClient) throw new Error('bigwater_backend not configured');
  const form = new FormData();
  form.append('taskId', input.category.key);
  form.append('file', input.file);
  try {
    // Content-Type is deliberately left unset — the browser/axios must
    // generate the multipart boundary itself; setting it manually breaks parsing.
    const { data } = await bigwaterClient.post<{ submission: BackendSubmissionRow }>(
      '/submissions/send',
      form,
      { headers: { Authorization: `Bearer ${privyAccessToken}` } },
    );
    return adaptSubmission(data.submission, input.category);
  } catch (err) {
    throw new Error(extractBigwaterError(err, 'Submission failed'));
  }
}

/**
 * Full submission history for the connected wallet, newest first — derivation
 * (filter/page/counts) happens client-side in `useSubmissionHistory`.
 */
export async function fetchSubmissionHistory(privyAccessToken: string): Promise<Submission[]> {
  if (!bigwaterClient) throw new Error('bigwater_backend not configured');
  const { data } = await bigwaterClient.get<{ submissions: BackendSubmissionHistoryRow[] }>(
    '/participants/submissions',
    { headers: { Authorization: `Bearer ${privyAccessToken}` } },
  );
  return data.submissions.map(adaptSubmissionHistoryEntry);
}

// ─── Participant profile (rail 2 — sustainability score/band/streak) ───────

export async function fetchParticipantProfile(
  privyAccessToken: string,
): Promise<ParticipantProfile> {
  if (!bigwaterClient) throw new Error('bigwater_backend not configured');
  const { data } = await bigwaterClient.get<{ profile: BackendParticipantProfile }>(
    '/participants/profile',
    { headers: { Authorization: `Bearer ${privyAccessToken}` } },
  );
  return adaptParticipantProfile(data.profile);
}

export interface TelegramLinkCode {
  code: string;
  expiresAt: string;
}

/** Issues a one-time code the user redeems via `t.me/<bot>?start=<code>` to
 *  link their Telegram account to the BigWater bot (bigwater_backend/rail2). */
export async function createTelegramLinkCode(
  privyAccessToken: string,
): Promise<TelegramLinkCode> {
  if (!bigwaterClient) throw new Error('bigwater_backend not configured');
  try {
    const { data } = await bigwaterClient.post<TelegramLinkCode>(
      '/telegram/link-code',
      {},
      {
        headers: { Authorization: `Bearer ${privyAccessToken}` },
        timeout: INTERACTIVE_TIMEOUT_MS,
      },
    );
    return data;
  } catch (err) {
    throw new Error(
      extractBigwaterError(err, "Couldn't start the Telegram bot connection. Please try again."),
    );
  }
}

// ─── Conversion (points → BIGW token / reward split) ───────────────────────
// NO BACKEND — bigwater_backend and bigw-api both lack a conversion/
// redemption endpoint (confirmed via a full route grep on both services).
// Mocked the same way fetchReferrals() is: no client check, module-level
// fixture, clearly flagged. Swap for a real call once one exists.

export const CONVERSION_THRESHOLD = 1000;
export const CONVERSION_SPLIT = { token: 30, rewards: 70 } as const;

const MOCK_CONVERSION_HISTORY: ConversionRequest[] = [
  {
    id: 'conv_1',
    points: 1000,
    requestedAt: new Date(Date.now() - 3 * 86_400_000).toISOString(),
    status: 'approved',
  },
  {
    id: 'conv_2',
    points: 1500,
    requestedAt: new Date(Date.now() - 10 * 86_400_000).toISOString(),
    status: 'rejected',
  },
];
let conversionHistory: ConversionRequest[] | null = null;

export async function fetchConversionHistory(): Promise<ConversionRequest[]> {
  await wait(MOCK_LATENCY_MS);
  conversionHistory ??= [...MOCK_CONVERSION_HISTORY];
  return conversionHistory;
}

export async function requestConversion(points: number): Promise<ConversionRequest> {
  await wait(MOCK_LATENCY_MS);
  const request: ConversionRequest = {
    id: `conv_${Date.now()}`,
    points,
    requestedAt: new Date().toISOString(),
    status: 'pending',
  };
  conversionHistory = [request, ...(conversionHistory ?? MOCK_CONVERSION_HISTORY)];
  return request;
}

// ─── Social linking (Telegram + Discord) ────────────────────────────────────

export interface TelegramAuthPayload {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

export async function linkTelegram(payload: TelegramAuthPayload): Promise<void> {
  if (!client) throw new Error('Backend not configured');
  await client.post('/social/telegram/link', payload, { timeout: INTERACTIVE_TIMEOUT_MS });
}

export async function unlinkTelegram(): Promise<void> {
  if (!client) throw new Error('Backend not configured');
  await client.delete('/social/telegram');
}

export async function startDiscordOAuth(): Promise<string> {
  if (!client) throw new Error('Backend not configured');
  const { data } = await client.get<{ authorize_url: string }>('/social/discord/oauth/start', {
    timeout: INTERACTIVE_TIMEOUT_MS,
  });
  return data.authorize_url;
}

export async function unlinkDiscord(): Promise<void> {
  if (!client) throw new Error('Backend not configured');
  await client.delete('/social/discord');
}

export async function startXOAuth(): Promise<string> {
  if (!client) throw new Error('Backend not configured');
  const { data } = await client.get<{ authorize_url: string }>('/social/x/oauth/start', {
    timeout: INTERACTIVE_TIMEOUT_MS,
  });
  return data.authorize_url;
}

export async function unlinkX(): Promise<void> {
  if (!client) throw new Error('Backend not configured');
  await client.delete('/social/x');
}

// ─── Backend availability ───────────────────────────────────────────────────

export const isBackendConfigured = !!client;
